import { defineStore } from 'pinia'
import api from '@/api'
import { getParams, isVip } from '@/utils'
import { DomainManager } from '@kuailu/sso'
import SSOClient from '@kuailu/sso'
import { buildTenantLocaleSwitcherOptions } from '@/views/login/components/utils'

function isH5Page() {
  return location.hash.indexOf('#/h5/') === 0
}

interface UserStoreState {
  token: string // 当前用户的 token
  userInfo: {
    mobile: string
    internationCode: string
    userName: string
  }
  /** 首页地址 */
  homeUrl: string
  /** 租户配置 (VIP场景使用) */
  tenantConfig: { empNoLoginEnable: boolean; loginMethods?: string[]; languageList?: string[] } | null
  /** 过滤后的语言选项 */
  filteredLocaleOptions: {value: string; label: string}[] | null
}

// 登录方式 getter 计算
export const useUserStoreGetters = (store: ReturnType<typeof useUserStore>) => {
  const fallbackLoginMethods = ['phonePassword', 'sms']
  const normalizedLoginMethods = computed(() => {
    const methods = store.tenantConfig?.loginMethods
    return Array.isArray(methods) && methods.length > 0 ? methods : fallbackLoginMethods
  })

  const hasLoginMethod = (method: string) => normalizedLoginMethods.value.includes(method)

  const enableSmsLogin = computed(() => hasLoginMethod('sms'))
  const enablePhonePasswordLogin = computed(() => hasLoginMethod('phonePassword'))
  const enableEmpNoLogin = computed(() => {
    const methods = store.tenantConfig?.loginMethods
    if (Array.isArray(methods) && methods.length > 0) {
      return hasLoginMethod('userNo')
    }
    return !!store.tenantConfig?.empNoLoginEnable
  })
  const enablePasswordLogin = computed(
    () => enablePhonePasswordLogin.value || enableEmpNoLogin.value
  )

  return {
    normalizedLoginMethods,
    enableSmsLogin,
    enablePhonePasswordLogin,
    enableEmpNoLogin,
    enablePasswordLogin
  }
}

export const useUserStore = defineStore('user', {
  state: () => {
    const ret = {
      token: '',
      userInfo: {
        mobile: '',
        internationCode: '',
        userName: ''
      },
      /** 在App.vue 创建之后就固定不动，因为内部路由会发生变化 */
      homeUrl: getParams('redirectUrl'),
      /** 租户配置 (VIP场景使用) */
      tenantConfig: null as { empNoLoginEnable: boolean; loginMethods?: string[]; languageList?: string[] } | null,
      /** 过滤后的语言选项 */
      filteredLocaleOptions: null as {value: string; label: string}[] | null
    } as UserStoreState
    const proxy = new Proxy(ret, {
      set(target, p, newValue, receiver) {
        if (p === 'homeUrl') {
          return false
        }

        return Reflect.set(target, p, newValue, receiver)
      }
    })
    return proxy
  },
  actions: {
    // 清除信息
    reset() {
      this.token = ''
      this.tenantConfig = null
      this.filteredLocaleOptions = null
    },
    // 退出登录
    async logout() {
      const _res: any = await api('/p/plat/user/logout', { token: this.token })
      if (_res?.code === '200') {
      }
    },
    goBackHome() {
      window.location.replace(this.homeUrl)
    },
    // 登录/注册 成功后跳转
    loginLocationReplace() {
      let redirectUrl = getParams('redirectUrl')

      // SAAS-36463 【优化】SSO登录跳转逻辑
      if (!redirectUrl) {
        if (isH5Page()) {
          // h5
          redirectUrl = `https://${DomainManager.getTargetDomain('client-h5')}`
        } else {
          // pc
          redirectUrl = `https://${DomainManager.getTargetDomain('client')}`
        }
      }

      window.location.replace(redirectUrl)
    },
    getAccount(_: string) {
      this.userInfo.mobile = _
    },
    getInternationCode(_: string) {
      this.userInfo.internationCode = _
    },
    getUserName(_: string) {
      this.userInfo.userName = _
    },
    /**
     * 获取租户配置（包含登录方式、语言列表）并过滤生成语言选项
     * 只在VIP场景调用
     */
    async fetchTenantConfig() {
      if (!isVip()) return
      const host = window.location.hostname.replace(/\.authvip(?!.*\.authvip)/g, '.vip')
      try {
        const res: any = await (api as any)(
          '/user/tenant/getTenantConfig',
          { customAllDomain: host },
          { hideErrMsgCodes: ['550'] }
        )
        if (res.code === '200') {
          this.tenantConfig = res.data || { empNoLoginEnable: false, loginMethods: [] }
          if (res.data && Array.isArray(res.data.languageList)) {
            // 过滤生成最终语言选项
            const fallback = SSOClient.baseLocaleOptions
            this.filteredLocaleOptions = buildTenantLocaleSwitcherOptions(fallback, res.data.languageList)
          }
        }
      } catch (e) {
        // 获取失败，保持默认
      }
    }
  },
  persist: true
})
